import React, { useState, useEffect } from 'react';
import { Building2, Search, Filter, UserPlus, X } from 'lucide-react';
import { supabase } from '../supabase';
import { formatDate } from '../utils/commissionCalc';

export default function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAffiliate, setFilterAffiliate] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAttributeModal, setShowAttributeModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load all companies with affiliate info
      const { data: companiesData } = await supabase
        .from('companies')
        .select(`
          id, name, created_at, subscription_status, tier, billing_interval,
          referred_by_affiliate_id, referral_captured_at
        `)
        .order('created_at', { ascending: false });

      // Load affiliates for lookup and dropdown
      const { data: affiliatesData } = await supabase
        .from('affiliates')
        .select('id, name, code, active')
        .order('name');

      // Enrich accounts with affiliate names
      const affiliateMap = {};
      (affiliatesData || []).forEach(a => {
        affiliateMap[a.id] = a;
      });

      const enrichedAccounts = (companiesData || []).map(company => ({
        ...company,
        affiliate: company.referred_by_affiliate_id
          ? affiliateMap[company.referred_by_affiliate_id]
          : null
      }));

      setAccounts(enrichedAccounts);
      setAffiliates(affiliatesData || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAttributeAccount = async () => {
    if (!selectedAccount) return;

    try {
      await supabase
        .from('companies')
        .update({
          referred_by_affiliate_id: selectedAffiliateId || null,
          referral_captured_at: selectedAffiliateId ? new Date().toISOString() : null
        })
        .eq('id', selectedAccount.id);

      setShowAttributeModal(false);
      setSelectedAccount(null);
      setSelectedAffiliateId('');
      loadData();
    } catch (error) {
      alert('Error updating attribution: ' + error.message);
    }
  };

  const openAttributeModal = (account) => {
    setSelectedAccount(account);
    setSelectedAffiliateId(account.referred_by_affiliate_id || '');
    setShowAttributeModal(true);
  };

  // Filter accounts
  const filteredAccounts = accounts.filter(account => {
    const matchesSearch = account.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAffiliate = filterAffiliate === 'all'
      || (filterAffiliate === 'organic' && !account.referred_by_affiliate_id)
      || account.referred_by_affiliate_id === filterAffiliate;
    const matchesStatus = filterStatus === 'all' || account.subscription_status === filterStatus;

    return matchesSearch && matchesAffiliate && matchesStatus;
  });

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading accounts...
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 3rem', boxSizing: 'border-box', maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontSize: '2rem',
          fontWeight: '700',
          color: '#e0e0e0',
          marginBottom: '0.5rem'
        }}>
          Attribution
        </h1>
        <p style={{ color: '#888' }}>
          View all Work Van accounts and manage affiliate attribution
        </p>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
      }}>
        <div style={{
          flex: '1 1 250px',
          position: 'relative'
        }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#888'
          }} />
          <input
            type="text"
            placeholder="Search accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.75rem',
              background: '#1a1a1a',
              border: '1px solid #2a2a2a',
              borderRadius: '8px',
              color: '#e0e0e0'
            }}
          />
        </div>

        <select
          value={filterAffiliate}
          onChange={(e) => setFilterAffiliate(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            minWidth: '180px'
          }}
        >
          <option value="all">All Affiliates</option>
          <option value="organic">Organic (No Affiliate)</option>
          {affiliates.map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            minWidth: '150px'
          }}
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="canceled">Canceled</option>
          <option value="past_due">Past Due</option>
        </select>
      </div>

      {/* Stats */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '8px',
          padding: '0.75rem 1.25rem',
          border: '1px solid #2a2a2a'
        }}>
          <span style={{ color: '#888', fontSize: '0.85rem' }}>Showing: </span>
          <span style={{ color: '#e0e0e0', fontWeight: '600' }}>{filteredAccounts.length}</span>
          <span style={{ color: '#888', fontSize: '0.85rem' }}> of {accounts.length}</span>
        </div>
        <div style={{
          background: '#4ecca320',
          borderRadius: '8px',
          padding: '0.75rem 1.25rem',
          border: '1px solid #4ecca340'
        }}>
          <span style={{ color: '#4ecca3', fontWeight: '600' }}>
            {accounts.filter(a => a.referred_by_affiliate_id).length}
          </span>
          <span style={{ color: '#888', fontSize: '0.85rem' }}> attributed</span>
        </div>
      </div>

      {/* Accounts Table */}
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        border: '1px solid #2a2a2a',
        overflowX: 'auto'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          minWidth: '650px'
        }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
              <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                Company
              </th>
              <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                Signed Up
              </th>
              <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                Tier / Status
              </th>
              <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                Affiliate
              </th>
              <th style={{ textAlign: 'center', padding: '1rem', color: '#888', fontWeight: '500' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAccounts.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
                  No accounts found
                </td>
              </tr>
            ) : (
              filteredAccounts.map(account => (
                <tr
                  key={account.id}
                  style={{
                    borderBottom: '1px solid #2a2a2a',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#242424'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontWeight: '600', color: '#e0e0e0' }}>
                      {account.name}
                    </div>
                  </td>
                  <td style={{ padding: '1rem', color: '#888', fontSize: '0.9rem' }}>
                    {formatDate(account.created_at)}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        background: '#3498db20',
                        color: '#3498db',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {account.tier}
                      </span>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        background: account.subscription_status === 'active' ? '#4ecca320' :
                          account.subscription_status === 'trialing' ? '#f7931e20' : '#e74c3c20',
                        color: account.subscription_status === 'active' ? '#4ecca3' :
                          account.subscription_status === 'trialing' ? '#f7931e' : '#e74c3c',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        textTransform: 'capitalize'
                      }}>
                        {account.subscription_status}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {account.affiliate ? (
                      <div>
                        <div style={{ color: '#4ecca3', fontWeight: '600' }}>
                          {account.affiliate.name}
                        </div>
                        <div style={{ color: '#666', fontSize: '0.8rem' }}>
                          via ?ref={account.affiliate.code}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#666' }}>Organic</span>
                    )}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <button
                      onClick={() => openAttributeModal(account)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.5rem 0.75rem',
                        background: '#2a2a2a',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#888',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#3a3a3a';
                        e.currentTarget.style.color = '#e0e0e0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#2a2a2a';
                        e.currentTarget.style.color = '#888';
                      }}
                    >
                      <UserPlus size={14} />
                      {account.affiliate ? 'Change' : 'Attribute'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Attribution Modal */}
      {showAttributeModal && selectedAccount && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #3a3a3a',
            padding: '2rem',
            maxWidth: '450px',
            width: '100%'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#e0e0e0' }}>
                Attribute Account
              </h2>
              <button
                onClick={() => {
                  setShowAttributeModal(false);
                  setSelectedAccount(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{
              background: '#242424',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontWeight: '600', color: '#e0e0e0', marginBottom: '0.25rem' }}>
                {selectedAccount.name}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#888' }}>
                Signed up {formatDate(selectedAccount.created_at)}
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0' }}>
                Attribute to Affiliate
              </label>
              <select
                value={selectedAffiliateId}
                onChange={(e) => setSelectedAffiliateId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#2a2a2a',
                  border: '1px solid #3a3a3a',
                  borderRadius: '6px',
                  color: '#e0e0e0'
                }}
              >
                <option value="">No Affiliate (Organic)</option>
                {affiliates.filter(a => a.active).map(affiliate => (
                  <option key={affiliate.id} value={affiliate.id}>
                    {affiliate.name} ({affiliate.code})
                  </option>
                ))}
              </select>
              <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                Select an affiliate to credit for this account, or choose "No Affiliate" for organic signups.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => {
                  setShowAttributeModal(false);
                  setSelectedAccount(null);
                }}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#e0e0e0',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAttributeAccount}
                style={{
                  flex: 2,
                  padding: '0.875rem',
                  background: '#ff6b35',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Save Attribution
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
